import React, { useState } from 'react'
import { Formik } from 'formik'
import { useLocation } from 'react-router-dom'
import { Typography } from '@mui/material'

import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { FetchErrorCodes } from '@e3dc-react/shell/Libs'
import { UserLevel } from 'src/State/User/UserModel'
import CreatePasswordForm, { PasswordForm } from './CreatePasswordForm'
import PasswordCreated from './PasswordCreated'
import { usePasswordFormInitializer } from 'src/Hooks/user'

export interface InvitedUser {
  ID: number
  username: string
  Name: string
  Vorname: string
  ipin: string
  level: UserLevel
  sapEmployeeID: number
  mailNotification: boolean
  ldapEmail: string
  installerCertified: boolean
  source: string
  Firmen?: {
    ID: number
    Name?: string
    Manager?: number
    sapCustomerID?: number
    shopPermission: boolean
  }
  Anschrift?: {
    ID: number
    PLZ?: string
    Land?: string
    Address?: string
    PlaceOR?: string
    Telefon?: string
    Email?: string
  }
}

interface TokenValidProps {
  user?: InvitedUser
}

export interface TokenValidFormData {
  username: string
  eMail: string
  userLevel: string
  phoneNumber: string
  firstName: string
  lastName: string
  address: string
  zipCode: string
  city: string
  country: string
  companyName?: string
  ipin: string
  eMailNotification: boolean
  password: string
  passwordRepeat: string
  gdprAccepted: boolean
  useShop: boolean
  couponPermission: boolean
}
/**
 * Component to display if create-password route is called with a valid token.
 * Displays the form or success message
 */
const TokenValid: React.FC<TokenValidProps> = ({ user }) => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResult()
  const [passwordCreated, setPasswordCreated] = useState(false)
  const { enqueueErrorStack } = useSnackbar()
  const location = useLocation()
  const token = new URLSearchParams(location.search).get('token') ?? ''
  const { initialValues, validationSchema } = usePasswordFormInitializer(user)

  const onSubmit = async (values: PasswordForm): Promise<void> => {
    const passwordPayload = {
      password: values.password,
    }

    const passwordPromise = authFetch({
      path: `users/create-password/${token}`,
      method: 'POST',
      payload: passwordPayload,
    })
      .then(_ => {
        setPasswordCreated(true)
      })
      .catch((error: AuthFetchError) => {
        if (error.response?.data.errno === FetchErrorCodes.PasswordPartOfBreach) {
          enqueueErrorStack(t('user.password-breach'))
        } else if (error.response?.data.errno === FetchErrorCodes.NewUserPasswordInvalid) {
          enqueueErrorStack(t('user.password-guidelines'))
        } else {
          enqueueErrorStack(t('shell.register.invitation.error.creation'))
        }
        console.error('Could not create password', error.response?.data.message)
      })

    Promise.all([passwordPromise])
  }

  return (
    <>
      {passwordCreated ? (
        <PasswordCreated />
      ) : (
        <>
          <Typography
            variant="h5"
            component={'body'}
            sx={theme => ({ marginBottom: theme.spacing(3), fontWeight: theme.typography.fontWeightRegular })}
          >
            {t('shell.register.invitation.description')}
          </Typography>
          <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
            <CreatePasswordForm userLevel={user?.level as UserLevel} />
          </Formik>
        </>
      )}
    </>
  )
}

export default TokenValid
